// Change orders on a locked job.
// Answers: what is changing against the revision the shop is building to, what does it do to
// the price and the hours, and once it is approved, what still has to be done about it?
// A change order is the only way a locked job changes. A warranty fix is not one: it is a
// card logged at no charge.

import { useMemo, useState } from 'react';
import { changeOrderDues, nextChangeOrderStates, purchasingAfterChange, revisionDelta, type ChangeOrderState } from '@zerogap/engine';
import { toast, updateRow, useRows, type Row } from '../../lib/api';
import { addManualCard, approveChangeOrder, declineChangeOrder, priorOf, startChangeOrder, type StoredChanges } from '../../lib/changeorders';
import { longDate, qty } from '../../lib/format';
import { useInventory } from '../../lib/inventory';
import { useSession } from '../../lib/session';
import { Btn, Edit, Empty, Field, Info, Ln, Money, Panel, Tag } from '../../ui/kit';
import type { JobCtx } from './Workspace';

const signed = (h: number | string | null | undefined) => {
  const n = Number(h ?? 0);
  return `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toFixed(2)} h`;
};

export function ChangeOrders({ ctx }: { ctx: JobCtx }) {
  const { data, r, jobId } = ctx;
  const s = useSession();
  const job = data.job!;
  const res = r.res!;
  const lib = r.lib!;
  const inv = useInventory();
  const orders = useRows('change_order', { eq: { job_id: jobId }, order: ['number'] });
  const revisions = useRows('revision', { eq: { job_id: jobId }, order: ['rev_no'] });
  const resolutions = useRows('resolution', { eq: { job_id: jobId } });
  const users = useRows('user');
  const reasons = s.setting<{ key: string; label: string }[]>('change_order_reasons', []);
  const stateNames = s.setting<Record<string, string>>('change_order_state_names', {});
  const boardStates = s.setting<string[]>('board_states', []);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const canEdit = s.canEdit('screen.jobs') && s.can('money');

  const open = orders.rows.find((o) => o.state === 'draft' || o.state === 'sent');
  const last = revisions.rows.at(-1);
  const prior = useMemo(() => {
    const from = open ? revisions.rows.find((x) => x.id === open.from_revision_id) ?? last : last;
    return priorOf(resolutions.rows.find((x) => x.id === from?.resolution_id));
  }, [open, revisions.rows, resolutions.rows, last]);
  const live = useMemo(() => (open && prior ? revisionDelta(prior, res) : null), [open, prior, res]);
  const onOpenOrders = useMemo(() => {
    const out: Record<string, string> = {};
    for (const l of inv.lines.filter((x) => x.job_id === jobId)) {
      const n = l.ordered.filter((o) => o.order.state !== 'received' && o.order.state !== 'closed').reduce((sum, o) => sum + Math.max(0, Number(o.qty) - Number(o.received)), 0);
      if (n > 0) out[l.material_id] = String(n);
    }
    return out;
  }, [inv.lines, jobId]);
  const livePurchasing = live ? purchasingAfterChange(live.materials, onOpenOrders) : [];
  const reasonLabel = (key: string | null | undefined) => reasons.find((x) => x.key === key)?.label ?? key ?? '';
  const stateName = (key: string) => stateNames[key] ?? key;
  const who = (id: string | null | undefined) => users.rows.find((u) => u.id === id)?.name ?? '';

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    if (!open || !prior || !live) return;
    if (!window.confirm(`Approve change order ${open.number}? The job locks as Rev ${revisions.rows.length}. Cards not yet pulled are regenerated; cards in progress are flagged for the worker.`)) return;
    const done = await approveChangeOrder({
      co: open, data, res, lib, prior, revNo: revisions.rows.length, userId: s.user?.id ?? null, boardStates,
      card: { capHours: Number(s.setting('card_cap_hours', 16)), floorHours: Number(s.setting('card_floor_hours', 4)) },
      onOpenOrders, reasonLabel: reasonLabel(open.reason),
    });
    const c = done.cards;
    toast(`Approved as Rev ${revisions.rows.length}.${c && c.removed + c.created + c.flagged > 0 ? ` Cards: ${c.removed} regenerated into ${c.created}, ${c.flagged} flagged.` : ''}`);
  };

  if (!last && orders.rows.length === 0) return null; // nothing is locked yet: there is nothing to change against

  return (
    <Panel
      label={`Change orders · ${orders.rows.length}`}
      right={<Info left>After a job is locked, a change order is how it changes: a reason, the price and hours against the prior revision, then approved or declined. Approved, it becomes the next revision. A warranty fix is never a change order.</Info>}
    >
      {orders.rows.length === 0 && <Empty>No change orders on this job.</Empty>}
      {orders.rows.map((co) => {
        const dues = changeOrderDues(co as any);
        const kept = (co.changes ?? {}) as StoredChanges;
        const isOpen = co.id === open?.id;
        const price = isOpen ? live?.price_delta_cents : co.price_delta_cents;
        const hrs = isOpen ? live?.hours_delta : co.hours_delta;
        const allItems = isOpen ? live?.items ?? [] : kept.items ?? [];
        const items = allItems.filter((i) => i.kind !== 'repriced');
        const repriced = allItems.length - items.length;
        const buying = isOpen ? livePurchasing : kept.purchasing ?? [];
        const rev = revisions.rows.find((x) => x.id === co.revision_id);
        return (
          <div key={co.id} style={{ borderTop: '1px solid var(--line, #ddd)', padding: '8px 0' }}>
            <Ln
              left={
                <>
                  <b>Change order {co.number}</b> · {reasonLabel(co.reason) || 'no reason given'} <Tag kind={co.state === 'approved' ? 'on' : co.state === 'declined' ? 'off' : 'un'}>{stateName(co.state)}</Tag>
                  {rev && <> · Rev {rev.rev_no}</>}
                  <div>
                    <small>
                      {co.description || 'No description'} · started {longDate(co.created_at)}
                      {co.decided_at ? ` · ${stateName(co.state).toLowerCase()} ${longDate(co.decided_at)}${who(co.decided_by) ? ` by ${who(co.decided_by)}` : ''}` : ''}
                    </small>
                  </div>
                </>
              }
              right={
                co.state === 'declined' ? undefined : (
                  <span className="nowrap">
                    <Money cents={price ?? null} sign /> · {signed(hrs)}
                    {isOpen && <small> so far</small>}
                  </span>
                )
              }
            />
            {(dues.invoice_line_due || dues.client_notice_due) && (
              <div className="row mid" style={{ gap: 8, margin: '4px 0' }}>
                {dues.invoice_line_due && (
                  <>
                    <Tag kind="un">
                      invoice line due · <Money cents={co.price_delta_cents} sign />
                    </Tag>
                    {canEdit && (
                      <Btn small onClick={() => void updateRow('change_order', co.id, { invoice_line_done_at: new Date().toISOString(), invoice_line_done_by: s.user?.id ?? null })}>
                        Invoice line done
                      </Btn>
                    )}
                  </>
                )}
                {dues.client_notice_due && (
                  <>
                    <Tag kind="un">client notice due</Tag>
                    {canEdit && (
                      <Btn small onClick={() => void updateRow('change_order', co.id, { client_notice_done_at: new Date().toISOString(), client_notice_done_by: s.user?.id ?? null })}>
                        Client told
                      </Btn>
                    )}
                  </>
                )}
              </div>
            )}
            {co.state !== 'declined' && allItems.length > 0 && (
              <details open={isOpen}>
                <summary>
                  <small>
                    {items.length} {items.length === 1 ? 'item' : 'items'} changed
                    {buying.length > 0 ? ` · ${buying.length} ${buying.length === 1 ? 'material' : 'materials'}` : ''}
                  </small>
                </summary>
                {items.map((i) => (
                  <Ln key={`${i.id}-${i.kind}`} sub left={`${i.number} · ${i.name}`} right={<small>{i.kind === 'changed' ? i.what.join('; ') : i.kind}</small>} />
                ))}
                {repriced > 0 && (
                  <div>
                    <small>
                      {repriced} other {repriced === 1 ? 'item' : 'items'} moved in price or hours with nothing changed on them: they share sheets or setups with an item that did change.
                    </small>
                  </div>
                )}
                {buying.map((p) => (
                  <Ln
                    key={p.material_id}
                    sub
                    left={p.description}
                    right={
                      <small className={p.flagged ? 'warn' : ''}>
                        {p.kind === 'add' ? `buy ${qty(p.qty)} more` : `needs ${qty(p.qty)} less`} {p.unit ?? ''}
                        {p.flagged ? ` · ${qty(p.on_open_orders)} is on an open order: decide what to do with it, nothing is cancelled for you` : ''}
                      </small>
                    }
                  />
                ))}
              </details>
            )}
            {kept.cards && kept.cards.rework.length > 0 && (
              <div className="warn" style={{ marginTop: 4 }}>
                Completed work touched by this change (never altered): {kept.cards.rework.map((x) => `card #${x.number} ${x.name}`).join('; ')}. If it needs redoing, add a rework card below.
              </div>
            )}
            {isOpen && canEdit && (
              <div className="row mid" style={{ gap: 6, marginTop: 6 }}>
                {nextChangeOrderStates(co.state as ChangeOrderState).includes('sent') && (
                  <Btn disabled={busy} onClick={() => void run(() => updateRow('change_order', co.id, { state: 'sent', sent_at: new Date().toISOString() }))}>
                    Mark sent to the client
                  </Btn>
                )}
                <Btn primary disabled={busy || !live} onClick={() => void run(approve)}>
                  Approve · lock as Rev {revisions.rows.length}
                </Btn>
                <Btn
                  disabled={busy}
                  onClick={() =>
                    window.confirm(`Decline change order ${co.number}? The job goes back to how it was at Rev ${last?.rev_no ?? 0}: items added since are removed and edits are undone.`) &&
                    void run(async () => {
                      await declineChangeOrder(co, s.user?.id ?? null);
                      toast('Declined. The job is back as it was, and locked.');
                    })
                  }
                >
                  Decline · put the job back
                </Btn>
                <small>The job is open for editing until this is approved or declined.</small>
              </div>
            )}
          </div>
        );
      })}

      {canEdit && !open && job.locked_at && last && (
        <div style={{ marginTop: 10 }}>
          <div className="form">
            <Field label="Reason">
              <select value={reason} onChange={(e) => setReason(e.target.value)}>
                <option value="">Pick a reason…</option>
                {reasons.map((x) => (
                  <option key={x.key} value={x.key}>
                    {x.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="What is changing" wide>
              <Edit value={description} placeholder="One line the client would recognise" onSave={(v) => setDescription(v)} />
            </Field>
          </div>
          <div className="row mid" style={{ gap: 6, marginTop: 6 }}>
            <Btn
              primary
              disabled={busy || !reason}
              onClick={() =>
                void run(async () => {
                  await startChangeOrder(data, last, reason, description, orders.rows);
                  setReason('');
                  setDescription('');
                  toast('Change order started. The job is open: make the change, then approve or decline it here.');
                })
              }
            >
              Start a change order
            </Btn>
            <small>Starts from Rev {last.rev_no}. The job opens for editing; the shop keeps building to Rev {last.rev_no} until this is approved.</small>
          </div>
        </div>
      )}

      {canEdit && last && (
        <div className="row mid" style={{ gap: 6, marginTop: 12 }}>
          <ManualCard jobId={jobId} origin="rework" label="Add a rework card" hint="Work already completed that has to be redone. Completed cards are never altered." queued={boardStates[0] ?? 'Queued'} />
          <ManualCard jobId={jobId} origin="warranty" label="Log a warranty fix" hint="No charge, and never a change order." queued={boardStates[0] ?? 'Queued'} />
        </div>
      )}
    </Panel>
  );
}

function ManualCard({ jobId, origin, label, hint, queued }: { jobId: string; origin: 'rework' | 'warranty'; label: string; hint: string; queued: string }) {
  return (
    <Btn
      small
      title={hint}
      onClick={async () => {
        const what = window.prompt(`${label}: what is the work? (${hint})`);
        if (!what?.trim()) return;
        const card = await addManualCard(jobId, origin, what.trim(), queued);
        toast(`Card #${card.number} is on the board${origin === 'warranty' ? ', marked no charge' : ''}.`);
      }}
    >
      {label}
    </Btn>
  );
}

/** What the office still owes on a job's approved change orders, for the job's header. */
export function ChangeOrderDues({ jobId }: { jobId: string }) {
  const orders = useRows('change_order', { eq: { job_id: jobId, state: 'approved' } });
  const due = orders.rows.map((co: Row) => ({ co, d: changeOrderDues(co as any) })).filter((x) => x.d.invoice_line_due || x.d.client_notice_due);
  if (due.length === 0) return null;
  return (
    <>
      {due.map(({ co, d }) => (
        <span key={co.id}>
          {d.invoice_line_due && <Tag kind="un">CO {co.number}: invoice line due</Tag>} {d.client_notice_due && <Tag kind="un">CO {co.number}: client notice due</Tag>}{' '}
        </span>
      ))}
    </>
  );
}
